import React, { ReactElement, useState, useRef } from 'react';
import { useSelector } from '@ecommerce/nest-ssr/common';
import classNames from 'classnames';
import DepartmentMenuItem from './components/department-menu-item/department-menu-item';
import SubMenu from 'modules/shared/layout/menu/menu-desktop/components/department-menu/components/sub-menu/sub-menu';
import MenuInterface, { MenuKeys } from 'modules/shared/layout/menu/interfaces/menu.interface';

import './department-menu.scss';

interface DepartmentMenuListProps {
    isOpenMenu: boolean;
    onClose: () => void;
    topPosition: number;
}

const DepartmentMenu: React.FC<DepartmentMenuListProps> = ({ isOpenMenu, topPosition }): ReactElement | null => {
    const menuItemsTree = useSelector((state: { menuItemsTree: MenuInterface[]; }) => state.menuItemsTree ?? []);
    const [activeDepId, setActiveDepId] = useState<number | null>(null);
    const [activeSubDep, setActiveSubDep] = useState<MenuInterface | null>(null);
    const menuCloseTimer = useRef<null | ReturnType<typeof setTimeout>>(null);

    if (menuItemsTree.length === 0) {
        return null;
    }

    const clearMenuTimeout = (): void => {
        if (menuCloseTimer.current) {
            clearTimeout(menuCloseTimer.current);
            menuCloseTimer.current = null;
        }
    };

    const onMenuEnter = (): void => {
        clearMenuTimeout();
    };

    const onMouseEnter = (item: MenuInterface): void => {
        clearMenuTimeout();
        setActiveDepId(item.id);
        setActiveSubDep(item);
    };

    const onMouseLeave = (): void => {
        menuCloseTimer.current = setTimeout(() => {
            setActiveDepId(null);
            setActiveSubDep(null);
        }, 0);
    };

    return (
        <>
            <ul
                id="department-menu"
                onMouseLeave={onMouseLeave}
                className={classNames({
                    'department-menu': true,
                    'department-menu--show': isOpenMenu
                })}
                style={{ height: `calc(100vh - ${topPosition}px)` }}
                data-section="menu-level-1"
            >
                {
                    menuItemsTree.map(item => (
                        <li
                            key={item.id}
                            onMouseEnter={(): void => onMouseEnter(item)}
                            className={classNames({
                                'active': item.id === activeDepId,
                                'has-submenu': item[MenuKeys.items]?.length
                            })}
                        >
                            <DepartmentMenuItem url={item.url} name={item[MenuKeys.name]} icon={item[MenuKeys.icon]} />
                            { Boolean(item[MenuKeys.items]?.length)
                                && (
                                    <ul>
                                        {item[MenuKeys.items]?.map(subItem => (
                                            <li key={subItem.id}>
                                                <a href={subItem.url}>
                                                    {subItem[MenuKeys.name]}
                                                </a>
                                                {Boolean(subItem[MenuKeys.items]?.length) && (
                                                    <ul>
                                                        {subItem[MenuKeys.items]?.map(catItem => (
                                                            <li key={catItem.id}>
                                                                <a href={catItem.url}>
                                                                    {catItem[MenuKeys.name]}
                                                                </a>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )
                            }
                            {Boolean(item[MenuKeys.banner]?.length) && (
                                <ul className="banner-items">
                                    {
                                        item[MenuKeys.banner]?.map(bannerItem => (
                                            <li key={bannerItem.url}>
                                                <a href={bannerItem.url} title={bannerItem.title} target={bannerItem.target ?? void 0}>
                                                    {bannerItem.title}
                                                </a>
                                            </li>
                                        ))
                                    }
                                </ul>
                            )}
                        </li>
                    ))
                }
            </ul>
            {(activeDepId && (activeSubDep && Boolean(activeSubDep[MenuKeys.items]?.length)))
                && <SubMenu
                    item={activeSubDep}
                    onMouseLeave={onMouseLeave}
                    onMouseEnter={onMenuEnter}
                    topPosition={topPosition}
                />}
        </>
    );
};

export default DepartmentMenu;
